import React, { createContext, useContext, type ReactNode } from "react";

// Types based on the mock response structure
export interface Participant {
  first_name: string;
  last_name: string;
  fundraiser_code: string;
  primary_division: {
    id: number;
    name: string;
  };
  secondary_division: {
    id: number;
    name: string;
  };
  supporter_count: number;
  current_earnings: number;
  fundraising_goal: number;
  profile_image_url: string;
}

export interface Fundraiser {
  organization_name: string;
  organization_logo_url: string;
  name: string;
  code: string;
  purpose_message: string;
  start_date: string;
  end_date: string;
  primary_theme_hex: string;
  secondary_theme_hex: string;
  primary_organization_hex: string;
  secondary_organization_hex: string;
}

export interface Product {
  name: string;
  description: string;
  base_price: number;
  categories: string[];
  images: string[];
  features: string[];
  variants: {
    style_variants: number[];
    color_variants: number[];
  };
}

export interface FundraiserData {
  participant: Participant;
  fundraiser: Fundraiser;
  product_by_sku: Record<string, Product>;
  sku_by_category: Record<string, { skus: number[] }>;
  recommendations_by_sku: Record<string, { skus: number[] }>;
}

interface FundraiserContextType {
  data: FundraiserData | null;
  loading: boolean;
  error: string | null;
}

const FundraiserContext = createContext<FundraiserContextType | undefined>(
  undefined
);

// Mock data - replace this with actual API call
const mockData: FundraiserData = {
  participant: {
    first_name: "Jane",
    last_name: "Doe",
    fundraiser_code: "SPRING2025",
    primary_division: {
      id: 1,
      name: "Team Red",
    },
    secondary_division: {
      id: 2,
      name: "5th Grade",
    },
    supporter_count: 34,
    current_earnings: 1350.75,
    fundraising_goal: 2000.0,
    profile_image_url: "https://example.com/images/jane.jpg",
  },
  fundraiser: {
    organization_name: "Lincoln Elementary",
    organization_logo_url: "https://example.com/logo.png",
    name: "Spring Fundraiser 2025",
    code: "SPRING2025",
    purpose_message: "Help us build a new library",
    start_date: "2025-03-01",
    end_date: "2025-04-15",
    primary_theme_hex: "#2a9d8f",
    secondary_theme_hex: "#f4a261",
    primary_organization_hex: "#1b3a4b",
    secondary_organization_hex: "#b5651d",
  },
  product_by_sku: {
    "42": {
      name: "Chocolate Chip Cookies",
      description: "Delicious classic chocolate chip cookies.",
      base_price: 12.99,
      categories: ["Cookies", "Baked Goods"],
      images: [
        "https://example.com/products/cookies-choc-main.jpg",
        "https://example.com/products/cookies-choc-hover.jpg",
      ],
      features: ["Made with real butter", "12 cookies per box"],
      variants: {
        style_variants: [43, 44, 47],
        color_variants: [39, 45],
      },
    },
    "43": {
      name: "Blue Tumbler",
      description: "Durable, insulated blue tumbler.",
      base_price: 20.0,
      categories: ["Tumblers", "Drinkware"],
      images: [
        "https://example.com/products/tumbler-blue-main.jpg",
        "https://example.com/products/tumbler-blue-hover.jpg",
      ],
      features: ["Keeps drinks hot or cold", "Dishwasher safe"],
      variants: {
        style_variants: [42, 44, 47],
        color_variants: [39, 45],
      },
    },
    "44": {
      name: "Blue Tumbler",
      description: "Durable, insulated blue tumbler.",
      base_price: 20.0,
      categories: ["Tumblers", "Drinkware"],
      images: [
        "https://example.com/products/tumbler-blue-main.jpg",
        "https://example.com/products/tumbler-blue-hover.jpg",
      ],
      features: ["Keeps drinks hot or cold", "Dishwasher safe"],
      variants: {
        style_variants: [42, 44, 47],
        color_variants: [39, 45],
      },
    },
    "45": {
      name: "Blue Tumbler",
      description: "Durable, insulated blue tumbler.",
      base_price: 20.0,
      categories: ["Tumblers", "Drinkware"],
      images: [
        "https://example.com/products/tumbler-blue-main.jpg",
        "https://example.com/products/tumbler-blue-hover.jpg",
      ],
      features: ["Keeps drinks hot or cold", "Dishwasher safe"],
      variants: {
        style_variants: [42, 44, 47],
        color_variants: [39, 45],
      },
    },
    "46": {
      name: "Chocolate Chip Cookies",
      description: "Delicious classic chocolate chip cookies.",
      base_price: 12.99,
      categories: ["Cookies", "Baked Goods"],
      images: [
        "https://example.com/products/cookies-choc-main.jpg",
        "https://example.com/products/cookies-choc-hover.jpg",
      ],
      features: ["Made with real butter", "12 cookies per box"],
      variants: {
        style_variants: [43, 44, 47],
        color_variants: [39, 45],
      },
    },
    "47": {
      name: "Blue Tumbler",
      description: "Durable, insulated blue tumbler.",
      base_price: 20.0,
      categories: ["Tumblers", "Drinkware"],
      images: [
        "https://example.com/products/tumbler-blue-main.jpg",
        "https://example.com/products/tumbler-blue-hover.jpg",
      ],
      features: ["Keeps drinks hot or cold", "Dishwasher safe"],
      variants: {
        style_variants: [42, 44, 47],
        color_variants: [39, 45],
      },
    },
    "48": {
      name: "Blue Tumbler",
      description: "Durable, insulated blue tumbler.",
      base_price: 20.0,
      categories: ["Tumblers", "Drinkware"],
      images: [
        "https://example.com/products/tumbler-blue-main.jpg",
        "https://example.com/products/tumbler-blue-hover.jpg",
      ],
      features: ["Keeps drinks hot or cold", "Dishwasher safe"],
      variants: {
        style_variants: [42, 44, 47],
        color_variants: [39, 45],
      },
    },
    "49": {
      name: "Blue Tumbler",
      description: "Durable, insulated blue tumbler.",
      base_price: 20.0,
      categories: ["Tumblers", "Drinkware"],
      images: [
        "https://example.com/products/tumbler-blue-main.jpg",
        "https://example.com/products/tumbler-blue-hover.jpg",
      ],
      features: ["Keeps drinks hot or cold", "Dishwasher safe"],
      variants: {
        style_variants: [42, 44, 47],
        color_variants: [39, 45],
      },
    },
  },
  sku_by_category: {
    Cookies: {
      skus: [39, 42, 44, 47],
    },
    Tumblers: {
      skus: [42, 43, 44, 47],
    },
  },
  recommendations_by_sku: {
    "42": {
      skus: [103, 107],
    },
    "43": {
      skus: [108, 109],
    },
  },
};

export const FundraiserProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // In a real app, you would fetch this data from an API
  // For now, we'll use the mock data
  const [data, setData] = React.useState<FundraiserData | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    // Simulate API call
    const fetchData = async () => {
      try {
        setLoading(true);
        // Simulate network delay
        await new Promise((resolve) => setTimeout(resolve, 500));
        setData(mockData);
        setError(null);
      } catch (err) {
        console.log(err);

        setError("Failed to fetch fundraiser data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <FundraiserContext.Provider value={{ data, loading, error }}>
      {children}
    </FundraiserContext.Provider>
  );
};

export const useFundraiser = () => {
  const context = useContext(FundraiserContext);
  if (context === undefined) {
    throw new Error("useFundraiser must be used within a FundraiserProvider");
  }
  return context;
};
